import { useCallback, useEffect, useState } from 'react';
import { Notify } from '../../../../../components/Notify';
import { Button } from '../../../../../components/Button';
import {
  CashMeError,
  createCashMeProposal,
  createCashMeSimulation,
  getCashMeLimiters,
} from '../../api';
import {
  CollateralStepValues,
  CompanyStepValues,
  IncomeStepValues,
  findConditional,
  maritalRules,
  ProponentStepValues,
  SimulationFormValues,
} from '../../schemas';
import {
  CashMeCollateralType,
  CashMeCreditPurpose,
  ICashMeLimiters,
  ICashMePendingItem,
  ICashMeSimulationResult,
  ICreateCashMeProposal,
} from '../../types';
import {
  ISimulationFieldError,
  SimulationForm,
} from '../SimulationForm/SimulationForm';
import { SimulationResult } from '../SimulationResult/SimulationResult';
import { CollateralStep } from '../steps/CollateralStep';
import { CompanyStep } from '../steps/CompanyStep';
import { IncomeStep } from '../steps/IncomeStep';
import { ProponentStep } from '../steps/ProponentStep';
import { ReviewStep } from '../steps/ReviewStep';
import { ProposalOutcome } from '../ProposalOutcome/ProposalOutcome';
import { WizardShell } from '../WizardShell/WizardShell';
import wizardStyles from '../WizardShell/WizardShell.module.css';

type FlowStage =
  | 'simulation'
  | 'simulationResult'
  | 'collateral'
  | 'proponent'
  | 'income'
  | 'review'
  | 'success'
  | 'duplicate';

interface ICashMeFlow {
  actionLeadId: string;
  leadName: string;
  leadEmail: string;
  leadPhone: string;
}

export function CashMeFlow({
  actionLeadId,
  leadName,
  leadEmail,
  leadPhone,
}: ICashMeFlow) {
  const [stage, setStage] = useState<FlowStage>('simulation');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [simulationInput, setSimulationInput] =
    useState<SimulationFormValues | null>(null);
  const [simulation, setSimulation] = useState<ICashMeSimulationResult | null>(
    null
  );
  const [collateralValues, setCollateralValues] =
    useState<CollateralStepValues | null>(null);
  const [proponentValues, setProponentValues] =
    useState<ProponentStepValues | null>(null);
  const [companyValues, setCompanyValues] = useState<CompanyStepValues | null>(
    null
  );
  const [incomeValues, setIncomeValues] = useState<IncomeStepValues | null>(
    null
  );
  const [proposalId, setProposalId] = useState<string>();
  const [pendingItems, setPendingItems] = useState<ICashMePendingItem[]>([]);
  const [proposalError, setProposalError] = useState<string>();
  const [limiters, setLimiters] = useState<ICashMeLimiters | null>(null);
  const [limitersFailed, setLimitersFailed] = useState(false);

  /* The form's options, terms and minimums are CashMe's per-account config,
     so nothing can be offered until they load. */
  const loadLimiters = useCallback(() => {
    setLimitersFailed(false);
    getCashMeLimiters()
      .then(setLimiters)
      .catch(() => setLimitersFailed(true));
  }, []);

  useEffect(() => {
    loadLimiters();
  }, [loadLimiters]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [stage]);

  const isLegalPerson = simulationInput?.personType === 'legal';

  function notifyError(error: unknown) {
    const message =
      error instanceof CashMeError
        ? error.message
        : 'Ocorreu um erro ao enviar seus dados. Por favor, tente novamente mais tarde.';
    Notify(message, 'error');
  }

  async function handleSimulation(
    values: SimulationFormValues
  ): Promise<ISimulationFieldError | void> {
    setIsSubmitting(true);
    try {
      const result = await createCashMeSimulation({
        actionLeadId,
        loanAmount: values.loanAmount,
        propertyAmount: values.propertyAmount,
        propertyState: values.propertyState,
        installmentPeriod: values.installmentPeriod,
        gracePeriod: values.gracePeriod,
        interestTable: values.interestTable,
        monetaryCorrection: values.monetaryCorrection,
        ...(values.index ? { index: values.index } : {}),
        name: values.name,
        document: values.document,
        email: values.email,
        birthDate:
          values.personType === 'natural' ? values.birthDate : undefined,
        phone: values.personType === 'legal' ? values.phone : undefined,
      });

      setCollateralValues((current) => {
        if (!current) return current;

        const untouchedValue =
          current.value === simulationInput?.propertyAmount;
        const untouchedState =
          current.address?.state === simulationInput?.propertyState;

        return {
          ...current,
          value: untouchedValue ? values.propertyAmount : current.value,
          address: {
            ...current.address,
            state: untouchedState
              ? values.propertyState
              : current.address?.state,
          },
        };
      });

      setSimulationInput(values);
      setSimulation(result);
      setStage('simulationResult');
    } catch (error) {
      if (error instanceof CashMeError && error.code === 'INVALID_EMAIL') {
        return { field: 'email', message: error.message };
      }
      notifyError(error);
    } finally {
      setIsSubmitting(false);
    }
  }

  function buildProposalPayload(): ICreateCashMeProposal | null {
    if (!simulationInput || !collateralValues || !incomeValues) return null;

    const collateralAddress = {
      zipCode: collateralValues.address.zipCode,
      number: collateralValues.address.number,
      street: collateralValues.address.street,
      district: collateralValues.address.district,
      city: collateralValues.address.city,
      state: collateralValues.address.state,
    };

    const proponentAddress = isLegalPerson
      ? companyValues?.useCollateralAddress
        ? collateralAddress
        : (companyValues?.address as typeof collateralAddress)
      : proponentValues?.useCollateralAddress
        ? collateralAddress
        : (proponentValues?.address as typeof collateralAddress);

    if (!proponentAddress) return null;

    const { asksCohabitation, needsPartner } = maritalRules(
      proponentValues?.maritalStatus,
      proponentValues?.cohabitation
    );

    return {
      actionLeadId,
      creditPurposes: [incomeValues.creditPurpose as CashMeCreditPurpose],
      proponent: {
        personType: isLegalPerson ? 'legal' : 'natural',
        name: simulationInput.name,
        document: simulationInput.document,
        email: simulationInput.email,
        phone: simulationInput.phone,
        address: proponentAddress,
        monthlyIncome: incomeValues.monthlyIncome,
        ...(isLegalPerson
          ? {
              companyType: companyValues?.companyType as any,
              responsibleName: companyValues?.responsibleName || undefined,
              annualBilling: (companyValues?.annualBilling || undefined) as any,
              shareholdings: (companyValues?.shareholdings ?? []).map(
                (item) => ({
                  document: item.document,
                  percentage: item.percentage,
                })
              ),
            }
          : {
              birthDate: simulationInput.birthDate,
              maritalStatus: proponentValues?.maritalStatus as any,
              matrimonialRegime: (proponentValues?.maritalStatus === 'MARRIED'
                ? proponentValues.matrimonialRegime || undefined
                : undefined) as any,
              cohabitation: asksCohabitation
                ? proponentValues?.cohabitation === 'true'
                : undefined,
              incomeType: (incomeValues.incomeType || undefined) as any,
              spouse: needsPartner
                ? (proponentValues?.spouse as any)
                : undefined,
            }),
      },
      collaterals: [
        {
          type: collateralValues.type as CashMeCollateralType,
          value: collateralValues.value,
          address: collateralAddress,
          financed: collateralValues.financed,
          ...(collateralValues.financed
            ? {
                financedBank: collateralValues.financedBank || undefined,
                financedDebitBalance:
                  collateralValues.financedDebitBalance || undefined,
              }
            : {}),
        },
      ],
    };
  }

  async function handleProposal() {
    const payload = buildProposalPayload();
    if (!payload) return;

    setIsSubmitting(true);
    setPendingItems([]);
    setProposalError(undefined);
    try {
      const result = await createCashMeProposal(payload);
      setProposalId(result.proposalId);
      setStage('success');
    } catch (error) {
      if (error instanceof CashMeError && error.isDuplicate) {
        setStage('duplicate');
        return;
      }
      if (error instanceof CashMeError) {
        setPendingItems(error.pendingItems ?? []);
        setProposalError(error.message);
        return;
      }
      notifyError(error);
    } finally {
      setIsSubmitting(false);
    }
  }

  if (!limiters) {
    return (
      <WizardShell
        title="Simule seu empréstimo com garantia de imóvel"
        helper={
          limitersFailed
            ? 'Não conseguimos carregar as condições do parceiro agora.'
            : 'Carregando as condições disponíveis...'
        }
      >
        {limitersFailed ? (
          <div className={wizardStyles.footerSingle}>
            <Button
              type="button"
              color="primary"
              text="Tentar novamente"
              onClick={loadLimiters}
            />
          </div>
        ) : (
          <div className="d-flex justify-content-center py-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Carregando...</span>
            </div>
          </div>
        )}
      </WizardShell>
    );
  }

  if (stage === 'simulation') {
    return (
      <SimulationForm
        limiters={limiters}
        defaultName={leadName}
        defaultEmail={leadEmail}
        defaultPhone={leadPhone}
        initialValues={simulationInput}
        isSubmitting={isSubmitting}
        onSubmit={handleSimulation}
      />
    );
  }

  if (stage === 'simulationResult' && simulation && simulationInput) {
    return (
      <SimulationResult
        simulation={simulation}
        interestTable={simulationInput.interestTable}
        installmentPeriod={simulationInput.installmentPeriod}
        idealIncomeCommitmentPercent={limiters.idealIncomeCommitmentPercent}
        onBack={() => setStage('simulation')}
        onContinue={() => setStage('collateral')}
      />
    );
  }

  if (stage === 'collateral' && simulationInput) {
    return (
      <CollateralStep
        limiters={limiters}
        conditional={findConditional(
          limiters,
          simulationInput.interestTable,
          simulationInput.monetaryCorrection,
          simulationInput.index
        )}
        loanAmount={simulationInput.loanAmount}
        defaultValues={{
          value: simulationInput.propertyAmount,
          ...collateralValues,
          address: {
            zipCode: collateralValues?.address?.zipCode ?? '',
            number: collateralValues?.address?.number ?? '',
            street: collateralValues?.address?.street ?? '',
            district: collateralValues?.address?.district ?? '',
            city: collateralValues?.address?.city ?? '',
            state:
              collateralValues?.address?.state ?? simulationInput.propertyState,
          },
        }}
        onBack={() => setStage('simulationResult')}
        onSubmit={(values) => {
          setCollateralValues(values);
          setStage('proponent');
        }}
      />
    );
  }

  if (stage === 'proponent' && collateralValues) {
    const collateralAddress = collateralValues.address as any;

    if (isLegalPerson) {
      return (
        <CompanyStep
          collateralAddress={collateralAddress}
          useCollateralAddressDefault={collateralValues.useMainProponentAddress}
          defaultValues={companyValues ?? {}}
          onBack={() => setStage('collateral')}
          onSubmit={(values) => {
            setCompanyValues(values);
            setStage('income');
          }}
        />
      );
    }

    return (
      <ProponentStep
        collateralAddress={collateralAddress}
        useCollateralAddressDefault={collateralValues.useMainProponentAddress}
        defaultValues={proponentValues ?? {}}
        onBack={() => setStage('collateral')}
        onSubmit={(values) => {
          setProponentValues(values);
          setStage('income');
        }}
      />
    );
  }

  if (stage === 'income') {
    return (
      <IncomeStep
        isLegalPerson={isLegalPerson}
        necessaryIncome={simulation?.necessaryIncome}
        defaultValues={incomeValues ?? {}}
        onBack={() => setStage('proponent')}
        onSubmit={(values) => {
          setIncomeValues(values);
          setStage('review');
        }}
      />
    );
  }

  if (stage === 'review') {
    const payload = buildProposalPayload();
    if (!payload) return null;

    return (
      <ReviewStep
        payload={payload}
        isSubmitting={isSubmitting}
        pendingItems={pendingItems}
        errorMessage={proposalError}
        onBack={() => {
          setPendingItems([]);
          setProposalError(undefined);
          setStage('income');
        }}
        onSubmit={handleProposal}
      />
    );
  }

  if (stage === 'duplicate') {
    return <ProposalOutcome variant="duplicate" />;
  }

  if (stage === 'success') {
    return <ProposalOutcome variant="success" proposalId={proposalId} />;
  }

  return null;
}
